import { useContext } from "react";
import {
  Checkbox,
  CheckboxGroup,
  Disclosure,
  HeaderBar,
  HStack,
  InfoToggletip,
  SearchField,
  Spacer,
  SymbolIcon,
  Tag,
  VStack,
} from "widgets";

import type { TrackFeatures } from "../../types.ts";
import { useTrackPanel } from "./hooks/use-track-panel.ts";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";
import style from "./TracksConfiguration.module.css";
import { updateTrackConfiguration, updateTrackConfigurationNu } from "./utils/update-track-configuration.ts";

export const TrackPanel = () => {
  const { trackConfiguration, setTrackConfiguration, selectedGroupIds, availableTrackConfiguration } =
    useContext(TrackConfigurationContext);

  const { selectedTracks, filteredTracks, selectedNuFeatures, filteredNuFeatureTracks, trackGroups, setFilterTerm } =
    useTrackPanel();

  const selectedTrackConfigurations = selectedGroupIds
    .map((groupId) => trackConfiguration.get(groupId))
    .filter((trackFeatures): trackFeatures is TrackFeatures => Boolean(trackFeatures));

  return (
    <VStack spacing={0} className={style.trackPanel} data-testid="track-panel">
      <HeaderBar
        title="Tracks"
        asPanelHeader
        actions={[<SearchField key="search-field-tracks" onChange={setFilterTerm} width={120} />]}
      />
      {selectedGroupIds.length === 0 ? (
        <EmptyTrackSelection />
      ) : filteredTracks.length + filteredNuFeatureTracks.length === 0 ? (
        <EmptyTrackSearch />
      ) : (
        <div className={style.configList}>
          <CheckboxGroup
            value={selectedTracks}
            onChange={(selection) => {
              const newTrackConfiguration = updateTrackConfiguration(
                trackConfiguration,
                selectedGroupIds,
                selectedTracks,
                selection,
              );
              setTrackConfiguration(newTrackConfiguration);
            }}
          >
            <VStack spacing={0}>
              {filteredTracks.map((track) => (
                <Checkbox
                  value={track.type}
                  key={track.type}
                  className={style.trackItem}
                  isIndeterminate={
                    !selectedTrackConfigurations.every((trackFeatures) =>
                      trackFeatures.defaultTypes.includes(track.type),
                    )
                  }
                >
                  <HStack
                    style={{
                      padding: "4px 5px",
                      justifyContent: "space-between",
                    }}
                  >
                    {track.name}
                  </HStack>
                </Checkbox>
              ))}
            </VStack>
          </CheckboxGroup>
          {filteredNuFeatureTracks.length > 0 && (
            <CheckboxGroup
              value={selectedNuFeatures.map((feature) => feature.id.toString())}
              onChange={(nuFeatureSelection) => {
                const newTrackConfiguration = updateTrackConfigurationNu(
                  availableTrackConfiguration,
                  trackConfiguration,
                  selectedGroupIds,
                  nuFeatureSelection,
                );
                setTrackConfiguration(newTrackConfiguration);
              }}
            >
              <Disclosure
                header={
                  <HStack style={{ flex: 1 }}>
                    DNA Features
                    <Spacer />
                    <Tag label={filteredNuFeatureTracks.length} />
                  </HStack>
                }
                headingStyle={{ marginTop: 30, marginRight: 5 }}
                panelStyle={{ marginLeft: 0 }}
              >
                <VStack spacing={0}>
                  {filteredNuFeatureTracks.map((track) => {
                    const hasTrackInAllSelectedGroups = selectedTrackConfigurations.every((trackFeatures) =>
                      trackFeatures.nuFeatureTypes.some((featureTrack) => featureTrack.id === track.id),
                    );

                    const availableGroups = selectedGroupIds.filter((groupId) =>
                      availableTrackConfiguration
                        .get(groupId)
                        ?.nuFeatureTypes.some((featureType) => featureType.id === track.id),
                    );

                    const trackAvailabilityLabel = availableGroups
                      .map((groupId) => trackGroups.get(groupId)?.organismName)
                      .join(", ");

                    const isAvailableEverywhere = availableGroups.length === selectedGroupIds.length;

                    return (
                      <Checkbox
                        key={track.id}
                        value={track.id.toString()}
                        className={style.trackItem}
                        isIndeterminate={!hasTrackInAllSelectedGroups}
                      >
                        <HStack
                          style={{
                            padding: "4px 5px",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          {track.name}
                          <HStack>
                            {track.count === 0 && <span className={style.track_no_data}>(No data)</span>}
                            {!isAvailableEverywhere && (
                              <InfoToggletip
                                title="Available for these genomes only"
                                content={trackAvailabilityLabel}
                              />
                            )}
                          </HStack>
                        </HStack>
                      </Checkbox>
                    );
                  })}
                </VStack>
              </Disclosure>
            </CheckboxGroup>
          )}
        </div>
      )}
    </VStack>
  );
};

const EmptyTrackSelection = () => {
  return (
    <div className={style.emptyTrackSelection}>
      <div>Select contig(s) in the left panel to see the available tracks</div>
      <SymbolIcon icon="reply" size={36} style={{ transform: "scaleY(-1)", color: "var(--primary-main)" }} />
    </div>
  );
};

const EmptyTrackSearch = () => {
  return (
    <div className={style.emptyTrackSelection}>
      <div>No tracks found</div>
    </div>
  );
};
